import React, { useState, useEffect, useCallback } from "react";
import AsyncSelect from "react-select/async";
import { MultiSelect } from 'primereact/multiselect';
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from 'primereact/dropdown';
import IntlCurrencyInput from "react-intl-currency-input";

import { asyncSelectCustomStyles } from "../../util/styles";
import { reactSelectIcons } from "../../util/reactSelectPrimeIcons";
import { statusOptions, genreOptions, userTypeOptions, maritalStatusOptions, childrenOptions, dormitorios } from "./FormOptions";
import { searchIncorporators as searchIncorporatorsService, searchCity as searchCityService } from "../../services/SearchService";

const currencyConfig = {
    locale: "pt-BR",
    formats: {
        number: {
            BRL: {
                style: "currency",
                currency: "BRL",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            },
        },
    },
};

const FilterMultiSelect = ({ label, value, options, onChange, placeholder = "Selecione" }) => (
    <div className="form-group">
        <label>{label}</label>
        <MultiSelect
            resetFilterOnHide
            value={value}
            options={options}
            onChange={onChange}
            style={{ width: "100%", borderRadius: '0px' }}
            placeholder={placeholder}
            display="chip"
            filter
            showClear
            maxSelectedLabels={3}
            selectedItemsLabel="{0} items selecionados"
            emptyFilterMessage="Nenhum resultado encontrado"
        />
    </div>
);

const FormFilters = (props) => {
    const { state, getProjects, handleProjetoChanged, handleCityChanged, handleArrayChanged, handleBedroomsChanged, handleChanged, handleSubmit, resetForm, searchBuildings, clearDefaultEmpreendimentos } = props;

    const [buscaIncorporadorasOptions, setBuscaIncorporadorasOptions] = useState([]);
    const [buscaCidadesOptions, setBuscaCidadesOptions] = useState([]);

    useEffect(() => { getProjects(); }, []);

    const handleSearchIncorporators = async (inputQuery) => {
        const data = await searchIncorporatorsService(inputQuery);
        setBuscaIncorporadorasOptions(data);
        return data;
    };

    const handleSearchCity = async (inputQuery) => {
        const data = await searchCityService(inputQuery);
        setBuscaCidadesOptions(data);
        return data;
    };

    const onMultiSelectChange = useCallback((name, e) => {
        const mappedValue = e.value?.map((item) => ({ value: item, label: item }));
        handleArrayChanged({ name, value: mappedValue });
    }, [handleArrayChanged]);

    const mapToSelectValue = (items) => items?.map((item) => ({ value: item, label: item }));

    return (
        <div id="form-filters-wrapper" style={{ overflowX: "hidden" }}>
            <div className="card filtro-mapa">
                <div className="card-body">

                    <div className="form-group">
                        <label>Projeto</label>
                        <Dropdown
                            options={state.projetos}
                            value={state.projeto}
                            onChange={handleProjetoChanged}
                            style={{ width: "100%", borderRadius: '0px' }}
                            placeholder="Selecione"
                            filter
                            emptyFilterMessage="Nenhum resultado encontrado"
                        />
                    </div>

                    <div className="form-group">
                        <label>Cidade</label>
                        <AsyncSelect
                            styles={asyncSelectCustomStyles}
                            placeholder="Selecione"
                            isMulti
                            closeMenuOnSelect={false}
                            components={reactSelectIcons}
                            defaultOptions={buscaCidadesOptions}
                            loadOptions={handleSearchCity}
                            onMenuClose={() => setBuscaCidadesOptions([])}
                            value={state.city}
                            onChange={(value) => handleCityChanged({ name: "city", value })}
                            loadingMessage={() => 'Carregando...'}
                            noOptionsMessage={() => "Digite para buscar..."}
                            inputProps={{ autoComplete: 'off' }}
                        />
                    </div>

                    <div className="form-group">
                        <label>Incorporadora</label>
                        <AsyncSelect
                            styles={asyncSelectCustomStyles}
                            placeholder="Digite"
                            isMulti
                            closeMenuOnSelect={false}
                            components={reactSelectIcons}
                            defaultOptions={buscaIncorporadorasOptions}
                            loadOptions={handleSearchIncorporators}
                            onMenuClose={() => setBuscaIncorporadorasOptions([])}
                            value={mapToSelectValue(state.incorporators)}
                            onChange={(value) => handleArrayChanged({ name: "incorporators", value })}
                            loadingMessage={() => 'Carregando...'}
                            noOptionsMessage={() => "Digite para buscar..."}
                        />
                    </div>

                    <div className="form-group">
                        <label>Empreendimento</label>
                        <AsyncSelect
                            styles={asyncSelectCustomStyles}
                            placeholder="Digite"
                            isMulti
                            closeMenuOnSelect={false}
                            components={reactSelectIcons}
                            defaultOptions={state.buscaEmpreendimentossDefaultOptions}
                            loadOptions={searchBuildings}
                            onMenuClose={clearDefaultEmpreendimentos}
                            value={mapToSelectValue(state.property_of_interest)}
                            onChange={(value) => handleArrayChanged({ name: "property_of_interest", value })}
                            loadingMessage={() => 'Carregando...'}
                            noOptionsMessage={() => "Digite para buscar..."}
                        />
                    </div>

                    <FilterMultiSelect
                        label="Status"
                        options={statusOptions}
                        value={state.status}
                        onChange={(e) => onMultiSelectChange("status", e)}
                    />

                    <div className="form-group">
                        <label>Quartos</label>
                        <br />
                        {dormitorios.map((qtdQuartos) => (
                            <div className="form-check checkbox-inline pr-1" key={`check_quarto_${qtdQuartos}`}>
                                <div className="d-flex align-items-center">
                                    <InputSwitch
                                        id={`check_quarto_${qtdQuartos}`}
                                        checked={state.bedrooms.includes(qtdQuartos)}
                                        onChange={() => handleBedroomsChanged(qtdQuartos)}
                                    />
                                    <label htmlFor={`check_quarto_${qtdQuartos}`} className="pl-1 mb-0 ms-2">
                                        {qtdQuartos}
                                    </label>
                                </div>
                            </div>
                        ))}
                    </div>

                    <FilterMultiSelect
                        label="Tipo Usuário"
                        options={userTypeOptions}
                        value={state.user_type}
                        onChange={(e) => onMultiSelectChange("user_type", e)}
                    />

                    <FilterMultiSelect
                        label="Idade"
                        options={state.ageOptions}
                        value={state.age}
                        onChange={(e) => onMultiSelectChange("age", e)}
                    />

                    <FilterMultiSelect
                        label="Gênero"
                        options={genreOptions}
                        value={state.genre}
                        onChange={(e) => onMultiSelectChange("genre", e)}
                    />

                    <FilterMultiSelect
                        label="Estado civil"
                        options={maritalStatusOptions}
                        value={state.marital_status}
                        onChange={(e) => onMultiSelectChange("marital_status", e)}
                    />

                    <FilterMultiSelect
                        label="Filhos"
                        options={childrenOptions}
                        value={state.children}
                        onChange={(e) => onMultiSelectChange("children", e)}
                    />

                    <div className="form-group">
                        <label className="d-block mb-1">Renda (mínima e máxima)</label>
                        <div className="row">
                            <div className="col-6">
                                <IntlCurrencyInput
                                    currency="BRL"
                                    config={currencyConfig}
                                    value={state.minIncome}
                                    onChange={(event, value) => handleChanged({ name: "minIncome", value })}
                                    className="form-control"
                                />
                            </div>
                            <div className="col-6">
                                <IntlCurrencyInput
                                    currency="BRL"
                                    config={currencyConfig}
                                    value={state.maxIncome}
                                    onChange={(event, value) => handleChanged({ name: "maxIncome", value })}
                                    className="form-control"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card-footer btn-filtro-mobile"
                    style={{
                        position: "sticky",
                        bottom: 0,
                        backgroundColor: "#FFF",
                        padding: "5px 10px",
                        boxShadow: "0 0 3px rgba(0,0,0, .2)",
                    }}
                >
                    <div className="row" id="container-btn-filtros">
                        <div className="col-6">
                            <button
                                className="btn btn-block background-brain"
                                onClick={handleSubmit}
                            >
                                {state.overlays.length > 0 ? "ATUALIZAR" : "BUSCAR"}
                            </button>
                        </div>
                        <div className="col-6">
                            <button
                                className="btn btn-block btn-default default-gray"
                                onClick={resetForm}
                            >
                                <i className="far fa-file"></i> LIMPAR
                            </button>
                        </div>
                        <hr />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FormFilters;